import { useCallback, useState } from 'react'
import {
  type BackgroundsPanelSettings,
  type SpreadBackgroundsPanelSettings,
  resolveBackgroundsPanelSettings,
} from '@/lib/backgrounds/backgrounds-panel-settings'

/**
 * The Backgrounds panel's Type, filters, Show filename, sort and Filters-expanded state, lifted
 * into `RightPanel` so they survive the panel closing — but only on the spread they were made on:
 * moving to another spread (or reloading) starts the panel fresh. Its results come back with it,
 * since they're derived from these settings and the cached query (see `useBackgroundsQuery`).
 */
export function useBackgroundsSettings(spreadId: string | null) {
  const [backgroundsSettingsBySpread, setBackgroundsSettingsBySpread] =
    useState<SpreadBackgroundsPanelSettings | null>(null)

  // Forget them the moment the spread changes, so coming back to an earlier spread starts fresh
  // too (not just the new one). Adjusting state during render is React's recommended way to reset
  // on a prop change — it re-renders before committing, so no stale frame is ever shown.
  if (backgroundsSettingsBySpread && backgroundsSettingsBySpread.spreadId !== spreadId) {
    setBackgroundsSettingsBySpread(null)
  }

  const settings = resolveBackgroundsPanelSettings(backgroundsSettingsBySpread, spreadId)
  const onSettingsChange = useCallback(
    (next: BackgroundsPanelSettings) =>
      setBackgroundsSettingsBySpread({ spreadId, settings: next }),
    [spreadId]
  )

  return { settings, onSettingsChange }
}
